
import { Component, Vue, Prop } from "vue-property-decorator"

import { ReportData, ReportDetails, ReportSection, ReportInsight } from "@/types/report"

import ReportCell from "./ReportCell.vue"
import { CaseColumn } from "@/types/caseColumns"
import { DisplayCell } from "@/types/dashboard"

interface LayoutRow {
    row: number,
    left: DisplayCell[],
    right: DisplayCell[]
}

@Component({
	components: {
		"report-cell": ReportCell
	}
})
export default class ReportView extends Vue {
	// --- Props ---

	@Prop({ required: true })
	section!: ReportSection

	@Prop({ required: true })
	data!: { [key: string]: ReportInsight }

	@Prop()
	columns!: { [key: string]: CaseColumn }

	// --- Computed Properties ---

	public get layoutRows (): LayoutRow[] {
		const groups = this.layout.groupBy("y")
		const max = this.layout.max("y")

		if (max === null) return []

		// Group cells into rows based on y value
		const rawRows = Array.from(
			{ length: max + 1 },
			(_, i) => ({
				row: i,
				left: groups[i.toString()] ? groups[i.toString()].findAndGet(c => c.x === 0 && c.contents && c.contents.insightId && c.contents.chartType, r => [r], []) : [],
				right: groups[i.toString()] ? groups[i.toString()].findAndGet(c => c.x === 1 && c.contents && c.contents.insightId && c.contents.chartType, r => [r], []) : []
			})
		)

		// Combine rows to account for rows in which multiple on one column span across a single on the other
		const combinedRows: LayoutRow[] = []
		let i = 0
		while (i < rawRows.length) {
			const row = rawRows[i]
			const maxHeight = Math.max(row.left.max("h") ?? 0, row.right.max("h") ?? 0)
			if (maxHeight === 0) {
				// ignore this row; it has no cells
			} else if (maxHeight === 1) {
				combinedRows.push(row) // Normal row.
			} else {
				const readToY = row.row + maxHeight - 1
				while (i < readToY) {
					// Add the subrow's cells to this one.
					i++
					const subrow = rawRows[i]
					if (subrow) {
						row.left = [...row.left, ...subrow.left]
						row.right = [...row.right, ...subrow.right]
					}
				}
				combinedRows.push(row)
			}
			i++
		}
		return combinedRows
	}

	private get layout (): DisplayCell[] {
		return this.section.contents.map(cell => ({
			x: cell.cellPosition.x,
			y: cell.cellPosition.y,
			w: cell.cellSize.width,
			h: cell.cellSize.height,
			id: cell.id!,
			contents: cell.contents
		}))
	}
}
